import React, { useState, useEffect } from "react";
import "./Tienda.css";

const Tienda = () => {
    const [busqueda, setBusqueda] = useState("");
    const [carrito, setCarrito] = useState([]);
    const [mostrarCarrito, setMostrarCarrito] = useState(false);
    const [productos, setProductos] = useState([]);
    const [productoSeleccionado, setProductoSeleccionado] = useState(null);
    const [paginaActual, setPaginaActual] = useState(1);
    const [productosPorPagina] = useState(15);

    useEffect(() => {
        fetchProductos();
    }, []);

    const fetchProductos = async () => {
        try {
            const response = await fetch("https://backend-vbfh.onrender.com/api/inventario");
            const data = await response.json();
            setProductos(data);
        } catch (error) {
            console.error("Error al obtener productos:", error);
        }
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0,
        }).format(price);
    };

    // Modificación aquí: Ahora se busca por nombre o por ID
    const productosFiltrados = productos.filter((producto) => {
        const busquedaLowerCase = busqueda.toLowerCase();
        return (
            busqueda === "" ||
            producto.nombre.toLowerCase().includes(busquedaLowerCase) ||
            producto.id.toString().toLowerCase().includes(busquedaLowerCase)
        );
    });

    // Lógica de paginación
    const indexUltimoProducto = paginaActual * productosPorPagina;
    const indexPrimerProducto = indexUltimoProducto - productosPorPagina;
    const productosActuales = productosFiltrados.slice(indexPrimerProducto, indexUltimoProducto);
    const totalPaginas = Math.ceil(productosFiltrados.length / productosPorPagina);

    const cambiarPagina = (numeroPagina) => {
        setPaginaActual(numeroPagina);
        // Scroll hacia arriba cuando cambia de página
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const paginaSiguiente = () => {
        if (paginaActual < totalPaginas) {
            setPaginaActual(paginaActual + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const paginaAnterior = () => {
        if (paginaActual > 1) {
            setPaginaActual(paginaActual - 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Generar números de página para mostrar
    const numerosPagina = [];
    const paginasAMostrar = 5; // Número máximo de páginas a mostrar en la paginación
    
    let inicioPaginas = Math.max(1, paginaActual - Math.floor(paginasAMostrar / 2));
    let finPaginas = Math.min(totalPaginas, inicioPaginas + paginasAMostrar - 1);
    
    // Ajustar si estamos cerca del final
    if (finPaginas - inicioPaginas + 1 < paginasAMostrar) {
        inicioPaginas = Math.max(1, finPaginas - paginasAMostrar + 1);
    }
    
    for (let i = inicioPaginas; i <= finPaginas; i++) {
        numerosPagina.push(i);
    }

    const agregarAlCarrito = (producto) => {
        // Verificar si el producto tiene stock disponible
        if (producto.cantidad <= 0) {
            alert(`El producto "${producto.nombre}" está agotado.`);
            return;
        }

        const productoEnCarrito = carrito.find((item) => item.id === producto.id);
        if (productoEnCarrito) {
            // Verificar que no se exceda el stock disponible
            if (productoEnCarrito.cantidad + 1 > producto.cantidad) {
                alert(`No hay suficiente stock para "${producto.nombre}".\nStock disponible: ${producto.cantidad}`);
                return;
            }
            setCarrito(
                carrito.map((item) =>
                    item.id === producto.id ? { ...item, cantidad: item.cantidad + 1 } : item
                )
            );
        } else {
            setCarrito([...carrito, { ...producto, cantidad: 1 }]);
        }
    };

    const modificarCantidad = (id, nuevaCantidad) => {
        if (nuevaCantidad <= 0) {
            eliminarDelCarrito(id);
        } else {
            // Verificar stock disponible
            const producto = productos.find(p => p.id === id);
            const productoEnCarrito = carrito.find(item => item.id === id);
            
            if (producto && nuevaCantidad > producto.cantidad) {
                alert(`No hay suficiente stock para este producto.\nStock disponible: ${producto.cantidad}`);
                return;
            }
            
            setCarrito(
                carrito.map((item) =>
                    item.id === id ? { ...item, cantidad: nuevaCantidad } : item
                )
            );
        }
    };

    const eliminarDelCarrito = (id) => {
        setCarrito(carrito.filter((item) => item.id !== id));
    };

    const vaciarCarrito = () => {
        setCarrito([]);
    };

    const calcularTotal = () => {
        return carrito.reduce((total, item) => total + item.precio * item.cantidad, 0);
    };

    const enviarPedidoWhatsApp = () => {
        if (carrito.length === 0) {
            alert("El carrito está vacío.");
            return;
        }

        const mensaje = carrito
            .map(
                (item) =>
                    `${item.nombre} - Cantidad: ${item.cantidad} - Precio: ${formatPrice(item.precio * item.cantidad)}`
            )
            .join("%0A"); // Salto de línea en URL
        const total = calcularTotal();
        const url = `https://wa.me/573106031424?text=Hola, quiero hacer el siguiente pedido:%0A${mensaje}%0ATotal: ${formatPrice(total)}`;
        window.open(url, "_blank");
    };

    const abrirModal = (producto) => {
        setProductoSeleccionado(producto);
    };

    const cerrarModal = () => {
        setProductoSeleccionado(null);
    };

    return (
        <div className="tienda-container">
            {/* Barra de búsqueda */}
            <div className="barra-busqueda-container">
                <input
                    type="text"
                    placeholder="Buscar productos por nombre o ID..."
                    value={busqueda}
                    onChange={(e) => {
                        setBusqueda(e.target.value);
                        setPaginaActual(1); // Resetear a primera página cuando se busca
                    }}
                    className="barra-busqueda"
                />
                <button className="boton-busqueda">🔍</button>
            </div>

            {/* Contenedor de productos */}
            <main className="productos-container">
                <h1>Catálogo de Productos</h1>
                
                {/* Información de paginación */}
                <div className="info-paginacion">
                    <p>
                        Mostrando {productosActuales.length} de {productosFiltrados.length} productos 
                        (Página {paginaActual} de {totalPaginas})
                    </p>
                </div>

                <div className="grid-productos">
                    {productosActuales.length > 0 ? (
                        productosActuales.map((producto) => {
                            const estaAgotado = producto.cantidad <= 0;
                            return (
                                <div 
                                    key={producto.id} 
                                    className={`producto-card ${estaAgotado ? 'producto-agotado' : ''}`} 
                                    onClick={() => abrirModal(producto)}
                                >
                                    <img
                                        src={producto.imagen}
                                        alt={producto.nombre}
                                        className="producto-imagen"
                                    />
                                    <div className="producto-info">
                                        <h2>{producto.nombre}</h2>
                                        <p className="producto-id">ID: {producto.id}</p> {/* Mostrar el ID */}
                                        <p className="producto-descripcion">{producto.descripcion}</p>
                                        <div className="producto-stock-info">
                                            <p className={`producto-stock ${estaAgotado ? 'stock-agotado' : 'stock-disponible'}`}>
                                                {estaAgotado ? 'AGOTADO' : `Stock: ${producto.cantidad}`}
                                            </p>
                                        </div>
                                        <p className="producto-precio">{formatPrice(producto.precio)}</p>
                                        <button 
                                            className={`boton-agregar ${estaAgotado ? 'boton-agotado' : ''}`} 
                                            onClick={(e) => { 
                                                e.stopPropagation(); 
                                                agregarAlCarrito(producto); 
                                            }}
                                            disabled={estaAgotado}
                                        >
                                            {estaAgotado ? 'Agotado' : 'Añadir al carrito'}
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <p className="mensaje-no-encontrado">No se encontraron productos.</p>
                    )}
                </div>

                {/* Paginación */}
                {totalPaginas > 1 && (
                    <div className="paginacion-container">
                        <button 
                            className={`boton-paginacion ${paginaActual === 1 ? 'boton-deshabilitado' : ''}`}
                            onClick={paginaAnterior}
                            disabled={paginaActual === 1}
                        >
                            ‹ Anterior
                        </button>
                        
                        {/* Mostrar primera página si no está en el rango */}
                        {inicioPaginas > 1 && (
                            <>
                                <button 
                                    className={`numero-pagina ${1 === paginaActual ? 'pagina-actual' : ''}`}
                                    onClick={() => cambiarPagina(1)}
                                >
                                    1
                                </button>
                                {inicioPaginas > 2 && <span className="puntos-suspensivos">...</span>}
                            </>
                        )}

                        {/* Números de página */}
                        {numerosPagina.map(numero => (
                            <button
                                key={numero}
                                className={`numero-pagina ${numero === paginaActual ? 'pagina-actual' : ''}`}
                                onClick={() => cambiarPagina(numero)}
                            >
                                {numero}
                            </button>
                        ))}

                        {/* Mostrar última página si no está en el rango */}
                        {finPaginas < totalPaginas && (
                            <>
                                {finPaginas < totalPaginas - 1 && <span className="puntos-suspensivos">...</span>}
                                <button 
                                    className={`numero-pagina ${totalPaginas === paginaActual ? 'pagina-actual' : ''}`}
                                    onClick={() => cambiarPagina(totalPaginas)}
                                >
                                    {totalPaginas}
                                </button>
                            </>
                        )}

                        <button 
                            className={`boton-paginacion ${paginaActual === totalPaginas ? 'boton-deshabilitado' : ''}`}
                            onClick={paginaSiguiente}
                            disabled={paginaActual === totalPaginas}
                        >
                            Siguiente ›
                        </button>
                    </div>
                )}
            </main>

            {/* Botón flotante del carrito */}
            <button className="boton-carrito" onClick={() => setMostrarCarrito(!mostrarCarrito)}>
                🛒 {carrito.length > 0 && <span className="contador-carrito">{carrito.length}</span>}
            </button>

            {/* Carrito desplegable */}
            {mostrarCarrito && (
                <div className="carrito-container">
                    <div className="carrito-titulo">
                        <h2>Carrito de Compras</h2>
                    </div>
                    {carrito.length > 0 ? (
                        <>
                            <ul className="lista-carrito">
                                {carrito.map((item) => {
                                    const productoInventario = productos.find(p => p.id === item.id);
                                    const stockDisponible = productoInventario ? productoInventario.cantidad : 0;
                                    const sinStock = item.cantidad > stockDisponible;
                                    
                                    return (
                                        <li key={item.id} className={`item-carrito ${sinStock ? 'item-sin-stock' : ''}`}>
                                            <img
                                                src={item.imagen}
                                                alt={item.nombre}
                                                className="imagen-carrito"
                                            />
                                            <div className="info-carrito">
                                                <h3>{item.nombre}</h3>
                                                <p>ID: {item.id}</p> {/* Mostrar el ID en el carrito */}
                                                <p>Precio: {formatPrice(item.precio)}</p>
                                                <div className="cantidad-container">
                                                    <button onClick={() => modificarCantidad(item.id, item.cantidad - 1)}>
                                                        -
                                                    </button>
                                                    <input
                                                        type="number"
                                                        value={item.cantidad}
                                                        onChange={(e) => modificarCantidad(item.id, parseInt(e.target.value))}
                                                    />
                                                    <button onClick={() => modificarCantidad(item.id, item.cantidad + 1)}>
                                                        +
                                                    </button>
                                                </div>
                                                <p>Total: {formatPrice(item.precio * item.cantidad)}</p>
                                                {sinStock && (
                                                    <p className="alerta-stock">
                                                        ⚠️ Stock insuficiente. Disponible: {stockDisponible}
                                                    </p>
                                                )}
                                            </div>
                                            <button className="boton-eliminar" onClick={() => eliminarDelCarrito(item.id)}>
                                                🗑️
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                            <p className="total-carrito">Total: {formatPrice(calcularTotal())}</p>
                            <button className="boton-vaciar" onClick={vaciarCarrito}>
                                Vaciar Carrito
                            </button>
                            <button className="boton-enviar" onClick={enviarPedidoWhatsApp}>
                                Enviar Pedido por WhatsApp
                            </button>
                        </>
                    ) : (
                        <p className="mensaje-carrito-vacio">El carrito está vacío.</p>
                    )}
                </div>
            )}

            {/* Modal de producto */}
            {productoSeleccionado && (
                <div className="modal-overlay" onClick={cerrarModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={cerrarModal}>×</button>
                        <img
                            src={productoSeleccionado.imagen}
                            alt={productoSeleccionado.nombre}
                            className="modal-imagen"
                        />
                        <h2>{productoSeleccionado.nombre}</h2>
                        <p className="modal-id">ID: {productoSeleccionado.id}</p> {/* Mostrar el ID en el modal */}
                        <p className="modal-descripcion">{productoSeleccionado.descripcion}</p>
                        <div className="modal-stock-info">
                            <p className={`modal-stock ${productoSeleccionado.cantidad <= 0 ? 'stock-agotado' : 'stock-disponible'}`}>
                                {productoSeleccionado.cantidad <= 0 ? 'AGOTADO' : `Stock disponible: ${productoSeleccionado.cantidad}`}
                            </p>
                        </div>
                        <p className="modal-precio">{formatPrice(productoSeleccionado.precio)}</p>
                        <button 
                            className={`boton-agregar ${productoSeleccionado.cantidad <= 0 ? 'boton-agotado' : ''}`} 
                            onClick={() => { 
                                agregarAlCarrito(productoSeleccionado); 
                                cerrarModal(); 
                            }}
                            disabled={productoSeleccionado.cantidad <= 0}
                        >
                            {productoSeleccionado.cantidad <= 0 ? 'Producto Agotado' : 'Añadir al carrito'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Tienda;